import type {DialogForm,DialogField} from './dialogForms';

/** Validate the closed JSON grammar before parsing mixed JavaScript sources. */
function jsonValue(source:string):unknown{
 let at=0;
 const space=()=>{while(/\s/.test(source[at]??'')&&at<source.length)at++;};
 const string=()=>{const match=source.slice(at).match(/^"(?:[^"\\]|\\(?:["\\/bfnrt]|u[0-9a-fA-F]{4}))*"/);if(!match||[...match[0]].some(char=>char.charCodeAt(0)<32))return false;at+=match[0].length;return true;};
 const value=(depth:number):boolean=>{
  space();if(depth>64)return false;
  if(source[at]==='"')return string();
  const open=source[at];
  if(open==='['||open==='{'){
   at++;space();const close=open==='['?']':'}';if(source[at]===close){at++;return true;}
   for(;;){if(open==='{'){space();if(!string())return false;space();if(source[at++]!==':')return false;}
    if(!value(depth+1))return false;space();if(source[at]===close){at++;return true;}if(source[at++]!==',')return false;
   }
  }
  const token=source.slice(at).match(/^(?:true|false|null|-?(?:0|[1-9]\d*)(?:\.\d+)?(?:[eE][+-]?\d+)?)/);
  if(!token)return false;at+=token[0].length;return true;
 };
 const valid=value(0);space();return valid&&at===source.length?JSON.parse(source):undefined;
}
/** Read balanced JSON literals only; executable JavaScript is never interpreted. */
function literal(source:string,start:number):string|undefined{
 let depth=0,quoted=false,escaped=false;
 for(let i=start;i<source.length;i++){
  const c=source[i];
  if(quoted){if(escaped)escaped=false;else if(c==='\\')escaped=true;else if(c==='"')quoted=false;continue;}
  if(c==='"')quoted=true;
  else if(c==='{'||c==='[')depth++;
  else if(c==='}'||c===']'){if(--depth===0)return source.slice(start,i+1);}
 }
 return undefined;
}
export function dynamicDialogEvidence(html:string):{forms:DialogForm[];missing:{dialog:string;source:string}[]}{
 const doc=new DOMParser().parseFromString(html,'text/html');
 const empty=Array.from(doc.querySelectorAll('dialog,[role="dialog"]')).filter(el=>!el.querySelector('input,select,textarea'));
 if(!empty.length)return {forms:[],missing:[]};
 const hasTemplate=Array.from(doc.querySelectorAll('template')).some(template=>Array.from(template.content.querySelectorAll('[name]')).some(el=>/^[a-z][a-zA-Z0-9_]*$/.test(el.getAttribute('name')??'')));
 const forms:DialogForm[]=[],definitions:{name:string;fields:unknown[]}[]=[];
 const visit=(value:unknown,path:string):void=>{
  if(!value||typeof value!=='object')return;
  if(Array.isArray(value)){if(value.length&&value.every(f=>f&&typeof f==='object'&&typeof f.name==='string'&&typeof f.type==='string')&&(/field|schema|form/i.test(path)||value.every(f=>typeof f.label==='string'||typeof f.title==='string')))definitions.push({name:path,fields:value});else value.forEach((v,i)=>visit(v,path+'.'+i));return;}
  const object=value as Record<string,unknown>;
  if(object.properties&&typeof object.properties==='object')definitions.push({name:path,fields:Object.entries(object.properties).map(([name,f])=>({...(f as object),name,required:Array.isArray(object.required)&&object.required.includes(name)}))});
  else for(const [key,item] of Object.entries(object))visit(item,path?path+'.'+key:key);
 };
 for(const script of Array.from(doc.querySelectorAll('script:not([src])'))){
  const text=script.textContent??'';
  if(script.getAttribute('type')==='application/json'){visit(jsonValue(text),script.id||'application/json');continue;}
  for(const match of text.matchAll(/(?:window\.)?([A-Za-z_$][\w$]*(?:\.[A-Za-z_$][\w$]*)*)\s*=\s*(?=\{\s*"|\[\s*\{\s*")/g)){
   const raw=literal(text,match.index!+match[0].length);
   if(raw)visit(jsonValue(raw),match[1]);
  }
 }
 for(const el of Array.from(doc.querySelectorAll('[data-form-schema],[data-fields]'))){
  const raw=el.getAttribute('data-form-schema')??el.getAttribute('data-fields');
  if(raw)visit(jsonValue(raw),'data-fields.'+(el.id||'schema'));
 }
 for(const definition of definitions){
  const fields:DialogField[]=[];
  for(const raw of definition.fields){
   const f=raw as Record<string,unknown>;
   if(typeof f.name!=='string'||! /^[a-z][a-zA-Z0-9_]*$/.test(f.name))continue;
   const type=f.type==='string'?(f.format==='date'?'date':'text'):f.type==='integer'?'number':f.type==='boolean'?'checkbox':f.type==='ref'?'entityref':f.type==='multiref'?'references':f.type;
   if(!['text','number','date','checkbox','select','textarea','entityref','references'].includes(String(type)))continue;
   const field:DialogField={name:f.name,type:type as DialogField['type'],label:typeof f.label==='string'?f.label:typeof f.title==='string'?f.title:f.name,required:f.required===true};
   for(const key of ['min','max','step'] as const){const value=f[key]??(key==='min'?(f.minimum??f.minLength):key==='max'?(f.maximum??f.maxLength):undefined);if(typeof value==='number'&&Number.isFinite(value))field[key]=value;}
   if(f.readOnly===true)field.readOnly=true;
   const options=f.options??f.enum;
   if(Array.isArray(options)&&options.every(v=>typeof v==='string')){field.type='select';field.options=options.map(value=>({value,label:value}));}
   fields.push(field);
  }
  if(fields.length===definition.fields.length&&fields.length)forms.push({title:definition.name,editTitle:definition.name,entity:definition.name.split('.').pop(),fields,note:'',actions:[],validation:[],success:''});
 }
 const incomplete=definitions.filter(def=>!forms.some(form=>form.title===def.name));
 const unique=[...new Map(forms.map(form=>[JSON.stringify(form),form])).values()];
 return {forms:unique,missing:[...incomplete.map(def=>({dialog:empty[0].getAttribute('aria-label')||empty[0].id||'dialog',source:def.name})),...empty.flatMap(el=>unique.length||incomplete.length||hasTemplate?[]:[{dialog:el.getAttribute('aria-label')||el.id||'dialog',source:el.id?'#'+el.id:'[role=dialog]'}])]};
}
